import { cva } from 'class-variance-authority'

import { cn } from '@/lib/utils'

import type { ReactNode } from 'react'

const headingVariants = cva('', {
  variants: {
    variant: {
      primary: 'font-heading font-extrabold uppercase',
      secondary: 'font-sans font-semibold text-secondary',
    },
    as: {
      h1: 'text-5xl leading-[48px] xl:text-[64px] xl:leading-[63px]',
      h2: 'text-4xl leading-10 xl:text-5xl xl:leading-[48px]',
      h3: 'text-3xl leading-[30px] xl:text-4xl xl:leading-10',
      h4: 'text-3xl leading-[30px] xl:text-4xl xl:leading-10 font-bold',
      h5: 'text-2xl leading-5 xl:text-3xl xl:leading-[30px] font-bold',
      h6: 'text-xl leading-4 xl:text-2xl xl:leading-5 font-bold',
    },
    colour: {
      default: 'text-grey-900',
      white: 'text-accent-6',
      primary: 'text-primary-500',
    },
  },
  compoundVariants: [
    {
      variant: 'secondary',
      as: 'h1',
      className: 'text-xl font-semibold text-secondary xl:text-2xl',
    },
    {
      variant: 'secondary',
      as: 'h2',
      className: 'text-lg font-semibold text-secondary xl:text-xl',
    },
  ],
  defaultVariants: {
    variant: 'primary',
    as: 'h2',
    colour: 'primary',
  },
})

type HeadingType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

type HeadingProps = React.HTMLAttributes<HTMLHeadingElement> & {
  level: HeadingType
  as?: HeadingType
  variant?: 'primary' | 'secondary'
  colour?: 'primary' | 'default' | 'white'
  children: ReactNode
}

export const H = ({ level, as = level, variant = 'primary', colour = 'primary', className, children, ...props }: HeadingProps) => {
  const Tag = level

  return (
    <Tag className={cn(headingVariants({ variant, as, colour }), className)} {...props}>
      {children}
    </Tag>
  )
}

type HProps = React.HTMLAttributes<HTMLHeadingElement> & {
  as?: HeadingType
  variant?: 'primary' | 'secondary'
  colour?: 'primary' | 'default' | 'white'
  children: ReactNode
}

const H1 = (props: HProps) => <H level="h1" {...props} />
const H2 = (props: HProps) => <H level="h2" {...props} />
const H3 = (props: HProps) => <H level="h3" {...props} />
const H4 = (props: HProps) => <H level="h4" {...props} />
const H5 = (props: HProps) => <H level="h5" {...props} />
const H6 = (props: HProps) => <H level="h6" {...props} />

export { H1, H2, H3, H4, H5, H6 }

// Keep the original Heading component for backward compatibility
export const Heading = ({
  as,
  variant = 'primary',
  colour = 'primary',
  size,
  children,
  ...props
}: {
  as: HeadingType
  size?: HeadingType
  variant?: 'primary' | 'secondary'
  colour?: 'primary' | 'default' | 'white'
  children: React.ReactNode
} & React.HTMLAttributes<HTMLHeadingElement>) => {
  return (
    <H level={as} as={size || as} variant={variant} colour={colour} {...props}>
      {children}
    </H>
  )
}
